import { buttonStyle } from 'components/buttons/buttonStyle';
import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import Big from 'big.js';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import InfoTooltip from 'components/InfoTooltip';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { ReactNode, useCallback, useId, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useApplyBulkDiscountMutation } from 'store/api/invoice-details';
import { FeatureFlag } from 'types/features';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { ReasonTooltip } from '../../components/ReasonTooltip';
import { useIsMultiApprovalFlow } from './invoice-matters/useCaseApproval';
import {
    CASE_CARD_CLASS,
    CASE_FIGURES_CLASS,
    Figure,
    SECTION_BODY,
    SECTION_CARD,
    SECTION_HEADER,
    SECTION_PANEL,
    SECTION_TITLE,
} from './SectionCard';

interface Props {
    invoice: InvoiceData;
    disabled?: boolean;
}

interface BulkDiscountForm {
    discountRate: number | null;
    discountAmount: string;
    areCostsDiscounted: boolean;
}

const INPUT_CLASS = 'bg-paper max-w-[120px]';

/**
 * Whether a joint invoice's Bulk discount may change: not once a case is approved, case by case, for a user who may
 * not change an approved one. Its key changes with any case's discount, so the form opens anew on the new values.
 */
export const useBulkDiscountGate = (invoice: InvoiceData) => {
    const isMultiApprovalWorkflowActive = useIsMultiApprovalFlow(invoice);
    const isUserPrivilegedToUpdateApproved = useHasPermissions(['UPDATE.INVOICES.STATE_APPROVED']);
    const matters = invoice.invoiceMatters ?? [];
    return {
        isDisabled:
            isMultiApprovalWorkflowActive &&
            matters.some((matter) => matter.approvedAt) &&
            !isUserPrivilegedToUpdateApproved,
        key: matters
            .map(
                (im) =>
                    `${im.matterId}:${im.calculationLog?.total?.matterDiscountRate ?? 0}:${im.calculationLog?.expensesWithVat?.discountApplicable ?? false}`
            )
            .join('|'),
    };
};

/**
 * One discount for every case of a joint invoice: the form opens on the cases' common rate, or empty where they
 * differ, and keeps its rate and amount in step over the fees, or the fees and expenses with Include expenses.
 */
const useBulkDiscount = (invoice: InvoiceData) => {
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    const isVatExemptExcludedFromDiscount = useIsFeatureEnabled(FeatureFlag.EXCLUDE_VAT_EXEMPT_FROM_DISCOUNT);
    const [applyBulkDiscount, { isLoading }] = useApplyBulkDiscountMutation();

    const feesBeforeDiscount = (invoice.invoiceMatters ?? []).reduce((sum, matter) => {
        const log = isPaymentCurrencyMode ? matter.calculationLog?.paymentCurrency : matter.calculationLog;
        const feesAfterMatterAdjustments = log?.fees?.totalAfterMatterAdjustments ?? 0;
        return sum + feesAfterMatterAdjustments;
    }, 0);

    const expensesBeforeDiscount = (invoice.invoiceMatters ?? []).reduce((sum, matter) => {
        const log = isPaymentCurrencyMode ? matter.calculationLog?.paymentCurrency : matter.calculationLog;
        const expensesWithVatAfterMatterAdjustments = log?.expensesWithVat?.totalAfterMatterAdjustments ?? 0;
        const vatExemptExpensesAfterMatterAdjustments = log?.expensesWithoutVat?.totalAfterMatterAdjustments ?? 0;
        const expensesAfterMatterAdjustments = isVatExemptExcludedFromDiscount
            ? expensesWithVatAfterMatterAdjustments
            : expensesWithVatAfterMatterAdjustments + vatExemptExpensesAfterMatterAdjustments;
        return sum + expensesAfterMatterAdjustments;
    }, 0);

    const matterDiscountRates = (invoice.invoiceMatters ?? []).map(
        (matter) => matter.calculationLog?.total?.matterDiscountRate ?? 0
    );
    const matterCostsDiscountedFlags = (invoice.invoiceMatters ?? []).map(
        (matter) => matter.calculationLog?.expensesWithVat?.discountApplicable ?? false
    );
    const isDiscountRateUniform =
        matterDiscountRates.length > 0 && matterDiscountRates.every((rate) => rate === matterDiscountRates[0]);
    const isAreCostsDiscountedUniform =
        matterCostsDiscountedFlags.length > 0 &&
        matterCostsDiscountedFlags.every((flag) => flag === matterCostsDiscountedFlags[0]);
    const hasMixedDiscount = matterDiscountRates.length > 1 && (!isDiscountRateUniform || !isAreCostsDiscountedUniform);
    const uniformDiscountRate = !hasMixedDiscount && isDiscountRateUniform ? matterDiscountRates[0] : null;
    const uniformAreCostsDiscounted =
        !hasMixedDiscount && isAreCostsDiscountedUniform ? matterCostsDiscountedFlags[0] : false;

    const initialDiscountRate = uniformDiscountRate ? uniformDiscountRate * 100 : null;
    const initialBase = uniformAreCostsDiscounted ? feesBeforeDiscount + expensesBeforeDiscount : feesBeforeDiscount;
    const initialDiscountAmount = initialDiscountRate
        ? new Big(Math.round(((initialBase * initialDiscountRate) / -100) * 100) / 100).toFixed(2)
        : '';

    const form = useForm<BulkDiscountForm>({
        defaultValues: {
            discountRate: initialDiscountRate,
            discountAmount: initialDiscountAmount,
            areCostsDiscounted: uniformAreCostsDiscounted,
        },
    });
    const { control, setValue, getValues } = form;

    const areCostsDiscounted = useWatch({ control, name: 'areCostsDiscounted' });
    const watchedDiscountRate = useWatch({ control, name: 'discountRate' });
    const showMixedDiscountHint = hasMixedDiscount && (watchedDiscountRate == null || `${watchedDiscountRate}` === '');

    const totalBeforeDiscount = useMemo(
        () => (areCostsDiscounted ? feesBeforeDiscount + expensesBeforeDiscount : feesBeforeDiscount),
        [areCostsDiscounted, feesBeforeDiscount, expensesBeforeDiscount]
    );

    const handleOnDiscountAmountChange = useCallback(
        (value: string | undefined) => {
            if (value && !isNaN(Number(value)) && totalBeforeDiscount) {
                const newDiscountAmount = Math.abs(Number(value));
                const newDiscountRate = (newDiscountAmount / totalBeforeDiscount) * 100;
                const roundingValue = Math.pow(10, 7);
                setValue('discountRate', Math.round(newDiscountRate * roundingValue) / roundingValue, {
                    shouldValidate: true,
                });
            } else {
                setValue('discountRate', 0, { shouldValidate: true });
            }
        },
        [setValue, totalBeforeDiscount]
    );

    const handleOnDiscountPercentageChange = useCallback(
        (value: string | undefined) => {
            if (value && !isNaN(Number(value)) && totalBeforeDiscount) {
                const newDiscountRate = Math.abs(Number(value));
                const newDiscountAmount = (totalBeforeDiscount * newDiscountRate) / -100;
                setValue('discountAmount', new Big(Math.round(newDiscountAmount * 100) / 100).toFixed(2));
            } else {
                setValue('discountAmount', '');
            }
        },
        [setValue, totalBeforeDiscount]
    );

    const handleAreCostsDiscountedChange = useCallback(
        (checked: boolean) => {
            const currentRate = getValues('discountRate');
            if (currentRate && !isNaN(Number(currentRate))) {
                const newDiscountRate = Math.abs(Number(currentRate));
                const newDiscountAmount =
                    ((checked ? feesBeforeDiscount + expensesBeforeDiscount : feesBeforeDiscount) * newDiscountRate) /
                    -100;
                setValue('discountAmount', new Big(Math.round(newDiscountAmount * 100) / 100).toFixed(2));
            } else {
                setValue('discountAmount', '');
            }
        },
        [getValues, setValue, feesBeforeDiscount, expensesBeforeDiscount]
    );

    const validateRate = (value: unknown) =>
        Math.abs(Number(value)) > 100 ? t('validation.cannot-exceed-pct', { max: '100' }) : true;

    const apply = (data: BulkDiscountForm) =>
        applyBulkDiscount({
            invoiceId: invoice.id,
            discountRate:
                data.discountRate == null || `${data.discountRate}` === '' ? null : Math.abs(Number(data.discountRate)),
            areCostsDiscounted: data.areCostsDiscounted,
        }).unwrap();

    return {
        form,
        apply,
        isLoading,
        isPaymentCurrencyMode,
        displayCurrency,
        totalBeforeDiscount,
        hasMixedDiscount,
        showMixedDiscountHint,
        handleOnDiscountAmountChange,
        handleOnDiscountPercentageChange,
        handleAreCostsDiscountedChange,
        validateRate,
    };
};

const BulkDiscountControl = ({ invoice, disabled = false }: Props) => {
    const { openModal, closeModal } = useModal();
    const {
        form: { control, handleSubmit },
        apply,
        isLoading,
        isPaymentCurrencyMode,
        displayCurrency,
        totalBeforeDiscount,
        showMixedDiscountHint,
        handleOnDiscountAmountChange,
        handleOnDiscountPercentageChange,
        handleAreCostsDiscountedChange,
        validateRate,
    } = useBulkDiscount(invoice);

    const onApply = handleSubmit(async (data) => {
        await apply(data).catch(console.error);
    });

    const handleApplyClick = () => {
        openModal(
            <ConfirmationModal
                titleId="invoices.totals-tab.bulk-discount.confirm-title"
                content={
                    <p className="text-body1">
                        <FormattedMessage id="invoices.totals-tab.bulk-discount.confirm-message" />
                    </p>
                }
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    void onApply();
                }}
            />,
            closeModal,
            { width: 400 }
        );
    };

    return (
        <ReasonTooltip reason={disabled ? t('invoices.totals-tab.bulk-discount.disabled-tooltip') : undefined}>
            <span className="block">
                <Collapsible disabled={disabled} className={cn(SECTION_CARD, disabled && 'bg-grey-50 text-grey-300')}>
                    <CollapsibleTrigger
                        className={cn(
                            SECTION_HEADER,
                            'group focus-visible:ring-ring/50 w-full rounded-md text-left outline-none focus-visible:ring-3 data-disabled:opacity-[0.38]'
                        )}
                    >
                        <span className="my-2.5 ml-2 flex flex-1 items-center gap-1">
                            <span className={SECTION_TITLE}>
                                <FormattedMessage id="invoices.totals-tab.bulk-discount.title" />
                            </span>
                            <InfoTooltip text="invoices.totals-tab.bulk-discount.tooltip" nested />
                        </span>
                        <ExpandMoreIcon className="text-grey-500 transition-transform group-data-[panel-open]:rotate-90" />
                    </CollapsibleTrigger>
                    <CollapsibleContent keepMounted className={SECTION_PANEL}>
                        <div className={SECTION_BODY}>
                            <div className="flex w-full flex-col gap-2">
                                <CheckboxInput
                                    control={control}
                                    name="areCostsDiscounted"
                                    label="invoices.totals-tab.bulk-discount.include-expenses"
                                    onChange={handleAreCostsDiscountedChange}
                                />
                                <div className="mb-4 flex items-center justify-between gap-2">
                                    <div className="w-[364px]">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-caption text-fg-secondary">
                                                    {t('invoices.details.total-before-discount')}
                                                </span>
                                                <MoneyDisplay amount={totalBeforeDiscount} currency={displayCurrency} />
                                            </div>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-caption text-fg-secondary">
                                                    {t('invoices.totals-tab.bulk-discount.rate-label')}
                                                </span>
                                                <div className="flex w-[152px] items-center gap-[0.5ch]">
                                                    <PercentageInput
                                                        allowNegative
                                                        name="discountRate"
                                                        control={control}
                                                        decimalPlaces={7}
                                                        noReadOnlyDivider
                                                        wrapperClassName={cn(
                                                            INPUT_CLASS,
                                                            isPaymentCurrencyMode && paymentCurrencyInputClassName
                                                        )}
                                                        onChange={(e) =>
                                                            handleOnDiscountPercentageChange(e.target.value)
                                                        }
                                                        validate={validateRate}
                                                    />
                                                    <span>%</span>
                                                </div>
                                            </div>
                                            {showMixedDiscountHint && (
                                                <div className="flex justify-end">
                                                    <span className="text-caption text-fg-secondary">
                                                        {t('invoices.totals-tab.bulk-discount.mixed-hint')}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-caption text-fg-secondary">
                                                    {t('invoices.details.discount-currency', {
                                                        currency: displayCurrency,
                                                    })}
                                                </span>
                                                <div className="flex items-center gap-[0.5ch]">
                                                    <DecimalInput
                                                        onlyNegative
                                                        name="discountAmount"
                                                        control={control}
                                                        noReadOnlyDivider
                                                        wrapperClassName={cn(
                                                            INPUT_CLASS,
                                                            isPaymentCurrencyMode && paymentCurrencyInputClassName
                                                        )}
                                                        onChange={(e) => handleOnDiscountAmountChange(e.target.value)}
                                                    />
                                                    <span>{displayCurrency}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex max-w-[364px] justify-end">
                                    <Button
                                        {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'w-[150px]')}
                                        onClick={handleApplyClick}
                                        disabled={isLoading}
                                    >
                                        <FormattedMessage id="invoices.totals-tab.bulk-discount.apply" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </span>
        </ReasonTooltip>
    );
};

export default BulkDiscountControl;

// Its prefix is a case card's: tabOfSection counts the edit on Cases, where the card is.
const BULK_DISCOUNT_SECTION_ID = 'case-bulk-discount';

/**
 * The new design's Bulk discount: a card that reads the cases' common discount until its Edit, then edits it in
 * place. Its Save asks for the confirmation classic asks for before it overwrites every case's discount. Without the right to change
 * an approved case it only reads, and says why.
 */
export const BulkDiscountCard = ({ invoice, disabled = false }: Props) => {
    const bulk = useBulkDiscount(invoice);
    const headingId = useId();
    const values = bulk.form.getValues();
    const description = (
        <p className="text-meta text-foreground-secondary m-0 max-w-[72ch]">
            <FormattedMessage id="invoices.totals-tab.bulk-discount.tooltip" />
        </p>
    );
    return (
        <SectionEdit
            id={BULK_DISCOUNT_SECTION_ID}
            titleId="invoices.totals-tab.bulk-discount.title"
            canEdit={!disabled}
            editor={(close) => <BulkDiscountEditor bulk={bulk} description={description} close={close} />}
        >
            {(edit) => (
                <section aria-labelledby={headingId} className={CASE_CARD_CLASS}>
                    <div className="flex items-start gap-2">
                        <div className="flex min-w-0 flex-auto flex-col gap-1">
                            <h2 id={headingId} className="text-section m-0">
                                <FormattedMessage id="invoices.totals-tab.bulk-discount.title" />
                            </h2>
                            {description}
                        </div>
                        {edit}
                    </div>
                    {disabled ? (
                        <p className="text-caption text-foreground-tertiary m-0">
                            <FormattedMessage id="invoices.totals-tab.bulk-discount.disabled-tooltip" />
                        </p>
                    ) : null}
                    <dl className={CASE_FIGURES_CLASS}>
                        <Figure labelId="invoices.details.total-before-discount">
                            <MoneyDisplay amount={bulk.totalBeforeDiscount} currency={bulk.displayCurrency} />
                        </Figure>
                        <Figure labelId="invoices.details.discount">
                            {bulk.hasMixedDiscount ? (
                                <FormattedMessage id="invoices.totals-tab.bulk-discount.mixed-hint" />
                            ) : (
                                <>
                                    {`${values.discountRate || 0}% · `}
                                    <MoneyDisplay amount={values.discountAmount || 0} currency={bulk.displayCurrency} />
                                </>
                            )}
                        </Figure>
                        <Figure labelId="invoices.totals-tab.bulk-discount.include-expenses">
                            <FormattedMessage id={values.areCostsDiscounted ? 'yes' : 'no'} />
                        </Figure>
                    </dl>
                </section>
            )}
        </SectionEdit>
    );
};

/** The confirmation a Save waits on: Apply settles it once the discount is applied, Cancel leaves the edit open. */
interface PendingApply {
    values: BulkDiscountForm;
    settle: (error?: unknown) => void;
}

const BulkDiscountEditor = ({
    bulk,
    description,
    close,
}: {
    bulk: ReturnType<typeof useBulkDiscount>;
    description: ReactNode;
    close: () => void;
}) => {
    const { form, apply, isLoading, displayCurrency, isPaymentCurrencyMode, totalBeforeDiscount, validateRate } = bulk;
    const [pending, setPending] = useState<PendingApply | null>(null);
    const amountClass = cn('w-44', isPaymentCurrencyMode && paymentCurrencyInputClassName);
    const dismiss = () => {
        pending?.settle();
        setPending(null);
    };
    const confirm = async () => {
        if (!pending) return;
        try {
            await apply(pending.values);
            close();
            pending.settle();
        } catch (error) {
            pending.settle(error);
        }
        setPending(null);
    };
    return (
        <>
            {description}
            <SectionEditForm
                methods={form}
                onSave={(values) =>
                    new Promise<void>((resolve, reject) =>
                        setPending({ values, settle: (error) => (error ? reject(error) : resolve()) })
                    )
                }
                onCancel={() => {
                    form.reset();
                    close();
                }}
            >
                <div className="flex flex-col gap-3">
                    <CheckboxInput
                        control={form.control}
                        name="areCostsDiscounted"
                        label="invoices.totals-tab.bulk-discount.include-expenses"
                        keepCheckbox
                        onChange={bulk.handleAreCostsDiscountedChange}
                    />
                    <div className="grid grid-cols-1 gap-x-6 gap-y-3 @xl:grid-cols-3">
                        <dl className="m-0">
                            <Figure labelId="invoices.details.total-before-discount">
                                <MoneyDisplay amount={totalBeforeDiscount} currency={displayCurrency} />
                            </Figure>
                        </dl>
                        <div className="flex flex-col gap-1">
                            <PercentageInput
                                allowNegative
                                name="discountRate"
                                control={form.control}
                                decimalPlaces={7}
                                label="invoices.totals-tab.bulk-discount.rate-label"
                                suffixComponent="%"
                                wrapperClassName={cn('w-32', isPaymentCurrencyMode && paymentCurrencyInputClassName)}
                                onChange={(e) => bulk.handleOnDiscountPercentageChange(e.target.value)}
                                validate={validateRate}
                            />
                            {bulk.showMixedDiscountHint ? (
                                <span className="text-caption text-foreground-tertiary">
                                    <FormattedMessage id="invoices.totals-tab.bulk-discount.mixed-hint" />
                                </span>
                            ) : null}
                        </div>
                        <DecimalInput
                            onlyNegative
                            name="discountAmount"
                            control={form.control}
                            label={t('invoices.details.discount-currency', { currency: displayCurrency })}
                            shouldTranslateLabel={false}
                            suffixComponent={displayCurrency}
                            wrapperClassName={amountClass}
                            onChange={(e) => bulk.handleOnDiscountAmountChange(e.target.value)}
                        />
                    </div>
                </div>
            </SectionEditForm>
            <AlertDialog
                open={!!pending}
                onOpenChange={(open) => {
                    if (!open) dismiss();
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            <FormattedMessage id="invoices.totals-tab.bulk-discount.confirm-title" />
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <FormattedMessage id="invoices.totals-tab.bulk-discount.confirm-message" />
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            <FormattedMessage id="cancel" />
                        </AlertDialogCancel>
                        <AlertDialogAction disabled={isLoading} onClick={() => void confirm()}>
                            <FormattedMessage id="invoices.totals-tab.bulk-discount.apply" />
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
